import {Injectable, signal, Injector} from '@angular/core';
import {Message, MessageService} from "primeng/api";
import { CustomMessageKey } from "../enums/custom-message-key.enum";
import { RequestDataService } from './request-data.service';
import { OriginMessageError } from '../enums/origin-message-error.enum';
import { UtilService } from './util.service';
import {Router} from "@angular/router";
import {HttpStatusCode} from "@angular/common/http";

@Injectable({
  providedIn: 'root'
})
// TODO mejorar la visualizacion de los mensajes, sean overlay, fixed. Mejorar p-message para que pueda controlarlo
export class CustomMessageService {
  unexpectedErrorDialog = signal<any>({});
  successDialog = signal<any>({});
  private errorLoggingService: any;

  // Constante para mensaje por defecto
  private static readonly DEFAULT_ERROR_TITLE = '¡Ups! Algo no sucedió como esperábamos';
  private static readonly DEFAULT_ERROR_MESSAGE = 'Parece que hubo un problema al cargar parte del contenido. Estamos revisando qué pasó para solucionarlo cuanto antes';

  constructor(
    private messageService: MessageService,
    private injector: Injector,
    private requestDataService: RequestDataService,
    private utilService: UtilService,
    private router: Router
  ) { }

  success(detail: string, key: string = CustomMessageKey.top_overlay) {
    this.load({ severity: 'success', summary: 'Éxito', detail, key});
  }

  error(detail: string, key: string = CustomMessageKey.top_overlay) {
    this.load({ severity: 'error', summary: 'Error', detail, key});
  }

  errors(detail: string[], key: string = CustomMessageKey.top_overlay) {
    let jsonMessageArray: Message[] = [];
    detail.forEach((text) => {
      jsonMessageArray.push({ severity: 'error', summary: 'Error', detail: text, key });
    });
    this.messageService.clear();
    this.messageService.addAll(jsonMessageArray);
  }

  comunicationError() {
    this.load({
      severity: 'error',
      summary: 'Error',
      detail: 'Hubo un error en la comunicación. Por favor, inténtalo de nuevo',
      key: CustomMessageKey.top_overlay
    });
  }

  responseError() {
    this.load({
      severity: 'error',
      summary: 'Error',
      detail: 'Hubo un error en la respuesta. Por favor, inténtalo de nuevo',
      key: CustomMessageKey.top_overlay
    });
  }

  load(jsonMessage: Message) {
    this.messageService.clear();
    this.messageService.add(jsonMessage);
  }

  clear() {
    this.messageService.clear();
  }

  /**
   * Obtiene ErrorLoggingService de forma lazy para evitar dependencias circulares
   */
  private getErrorLoggingService(): any {
    if (!this.errorLoggingService) {
      try {
        // Importación dinámica para evitar dependencias circulares
        const ErrorLoggingService = (window as any).__ErrorLoggingService;
        if (ErrorLoggingService) {
          this.errorLoggingService = this.injector.get(ErrorLoggingService);
        }
      } catch (e) {
        console.warn('ErrorLoggingService no disponible:', e);
      }
    }
    return this.errorLoggingService;
  }

  /**
   * Procesa y guarda el caso de error, retorna promise con el caseId
   */
  private async processErrorCase(
    requestData?: any,
    responseData?: any,
    additionalData?: any
  ): Promise<string> {

    const loggingService = this.getErrorLoggingService();

    // Obtener información de request (puede ser null)
    const request = await loggingService.getRequestInfoIfExists();
    // Incorporar 'request' en additionalData
    const mergedAdditionalData = {
      ...additionalData,
      ...request
    };

    const caseId = mergedAdditionalData.logOnly
      ? mergedAdditionalData.requestId
      : loggingService.generateCaseId();

    const errorCase = loggingService.createErrorCase(
      caseId,
      requestData,
      responseData,
      mergedAdditionalData
    );

    loggingService.saveErrorCase(errorCase).subscribe();

    return caseId;
  }
  // TODO deprecado
  unexpectedError(error: any, requestData?: any, responseData?: any) {
    this.processErrorCase(requestData, responseData, error).then(caseId => {
      this.unexpectedErrorDialog.set({
        ...error,
        error: {
          ...(error.error || {}),
          caseId: caseId
        }
      });
    });
  }

  // TODO deprecado
  errorHandler(message: string, data: any, identifier: string, requestData?: any) {
    this.processErrorCase(requestData, data, { message, identifier }).then(caseId => {
      this.unexpectedErrorDialog.set({
        error: {
          fe_tracking_number: identifier,
          caseId: caseId,
          data: data
        }
      });
      console.error(identifier, message, data, 'Case ID:', caseId);
    });
  }

  // TODO deprecado
  responseErrorHandler(data: any, identifier: string, requestData?: any) {
    this.processErrorCase(requestData, data, { identifier }).then(caseId => {
      this.unexpectedErrorDialog.set({
        error: {
          fe_tracking_number: identifier,
          caseId: caseId,
          data: data
        }
      });
      console.error(identifier, 'HTTP code no esperado:' + data.status, data, 'Case ID:', caseId);
    });
  }

  responseNoticeHandler(responseData: any, origin: string = OriginMessageError.BACKEND, template?: any) {
    this.handler('response', responseData, origin, template)
  }

  logErrorOnly(responseData: any, origin: string = OriginMessageError.BACKEND) {
    this.handler('log', responseData, origin)
  }

  page404(responseData: any) {
    this.handler('page404', responseData, OriginMessageError.FRONTEND)
  }

  private handler(type: string, responseData: any, origin: string, template?: any) {
    const url = this.getCurrentUrl();
    const title = template?.title && template.title.trim() ? template.title : CustomMessageService.DEFAULT_ERROR_TITLE;
    let message = template?.message && template.message.trim() ? template.message : CustomMessageService.DEFAULT_ERROR_MESSAGE;
    const severidad = template?.severidad || 'ERROR';
    template = { ...template, title, message}

    let requestData = null

    if (origin === OriginMessageError.BACKEND) {
      message = responseData?.error?.message !== undefined ? responseData?.error?.message : message;
      const traceFrontendId = responseData?.body?.traceFrontendId || responseData?.traceFrontendId || responseData?.error?.traceFrontendId;
      if (traceFrontendId) {
        requestData = this.requestDataService.getRequestData(traceFrontendId);
        this.requestDataService.clearRequestData(traceFrontendId);
      }
    }else{
      message = responseData?.message !== undefined ? responseData?.message : message;
    }

    this.processErrorCase({...requestData, template}, responseData,
      { message, url, severidad }).then(caseId => {
        if(type === 'response' && responseData.status == HttpStatusCode.Unauthorized) {
          return;
        }

        switch (type) {
          case 'page404':
            let currentUrl = this.utilService.getCurrentUrl();
            const state = { attemptedUrl: currentUrl, caseId: caseId };
            this.router.navigate(['/pagina-no-encontrada'], { state });
            break;
          case 'response':
            this.unexpectedErrorDialog.set({...template, caseId});
            break;
        }
    });
  }

  successHandler({title, description, buttonTitle, icon = false, iconPos = '', callback} : {
    title: string,
    description: string,
    buttonTitle: string,
    icon: boolean,
    iconPos?: 'left' | 'right' | '',
    callback?: () => void
  }) {
    this.successDialog.set({ title, description, buttonTitle, icon, iconPos, callback });
  }

  clearSuccessDialog() {
    this.successDialog.set({});
  }

  private getCurrentUrl(): string {
    return window.location.href;
  }
}
